import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Sessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sessionId) => {
    setDeleting(sessionId);
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete session');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Dashworx</h1>
          </div>
          <Button
            data-testid="back-to-home-btn"
            variant="ghost"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-4">Saved Sessions</h1>
            <p className="text-lg text-muted-foreground">
              Access your previously tailored resumes and cover letters
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No Saved Sessions</h3>
              <p className="text-muted-foreground mb-6">
                Start tailoring your resume to save sessions
              </p>
              <Button onClick={() => navigate('/wizard')}>
                Start Tailoring
              </Button>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="sessions-list">
              {sessions.map((session) => (
                <Card key={session.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {formatDate(session.created_at)}
                        </span>
                      </div>
                      
                      <div className="mb-4">
                        <h3 className="font-semibold mb-2">Job Description Preview</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {session.job_description.substring(0, 200)}...
                        </p>
                      </div>

                      {session.analysis && (
                        <div className="flex flex-wrap gap-2">
                          {session.analysis.skills && session.analysis.skills.length > 0 && (
                            <Badge variant="secondary">
                              {session.analysis.skills.length} Skills
                            </Badge>
                          )}
                          {session.analysis.tools && session.analysis.tools.length > 0 && (
                            <Badge variant="secondary">
                              {session.analysis.tools.length} Tools
                            </Badge>
                          )}
                          {session.tools_to_validate && session.tools_to_validate.length > 0 && (
                            <Badge variant="outline" className="border-orange-300 text-orange-700 dark:text-orange-400">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {session.tools_to_validate.length} To Validate
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>

                    <Button
                      data-testid={`delete-session-${session.id}-btn`}
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(session.id)}
                      disabled={deleting === session.id}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {deleting === session.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sessions;
